<script setup>
/**
 * [evotek P31 B3] Fork of upstream Logo.vue
 *
 * Upstream behavior: render globalConfig.logoThumbnail (installation-level logo)
 *   or fall back to inline SVG (evochat/Chatwoot bubble icon).
 *
 * [P31 B3] Per-account brand logo priority (highest first):
 *   1. account.brand_logo_url — ActiveStorage upload (new P31 B3)
 *   2. brand_theme.logoUrl   — manual URL under /customer-logos/ (P30 legacy)
 *   3. globalConfig.logoThumbnail — installation-level logo (upstream)
 *   4. Inline SVG fallback    — no logo configured anywhere
 *
 * Sources 1 & 2 are read from `accounts/getAccount(currentAccountId)` (accounts
 * Vuex store). This updates reactively when `store.dispatch('accounts/get')` is
 * called after logo upload/delete, ensuring Logo.vue re-renders immediately.
 *
 * IMPORTANT fallback guarantee: accounts without brand logo or brand_theme
 * (e.g. Evotek demo acc 2) fall through to globalConfig.logoThumbnail or
 * SVG — identical to upstream behavior. No regression for non-brand accounts.
 *
 * Upstream file: app/javascript/dashboard/components-next/icon/Logo.vue
 * Upstream commit: v4.13.0
 * Rebase note: when upgrading upstream, check if Logo.vue has changed logic
 *   (unlikely — it is a minimal component). If globalConfig getter name changes,
 *   update `useMapGetter('globalConfig/get')` accordingly.
 *   This fork is a MINIMAL delta: added `computed logoSrc` + prioritized `:src`.
 */
import { computed } from 'vue';
import { useAttrs } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

const attrs = useAttrs();
const globalConfig = useMapGetter('globalConfig/get');

// [P31 B3] Read current account from the accounts store (not auth store).
// Reason: `accounts/get` dispatch (called after logo upload/delete) updates
// `accounts` state via ADD_ACCOUNT mutation. `auth/getCurrentAccount` reads from
// `currentUser.accounts` (auth state) which is NOT updated by `accounts/get`.
// Using `accounts/getAccount(accountId)` ensures reactivity after upload.
const currentAccountId = useMapGetter('getCurrentAccountId');
const getAccountFn = useMapGetter('accounts/getAccount');
const currentAccount = computed(() =>
  currentAccountId.value ? getAccountFn.value(currentAccountId.value) : null
);

/**
 * Resolve the logo URL to display.
 *
 * Priority:
 *   1. account.brand_logo_url (ActiveStorage — uploaded via B3)
 *   2. account.custom_attributes.brand_theme.logoUrl (manual /customer-logos/ path)
 *   3. globalConfig.logoThumbnail (installation level)
 *   4. null → render SVG fallback
 *
 * Validation (defence-in-depth):
 *   - For source 1: must start with /rails/active_storage/ — Rails controls this path.
 *   - For source 2: must start with /customer-logos/ — nginx static, server-controlled.
 *   - Both: reject values containing quotes, parens, backslash, whitespace (injection guard).
 *   - Using :src binding instead of CSS content:url() removes CSS injection surface
 *     for this component, but we still reject unsafe chars as a defence-in-depth.
 */
const logoSrc = computed(() => {
  const account = currentAccount.value;

  if (account) {
    // Source 1: ActiveStorage uploaded logo
    const asUrl = account.brand_logo_url;
    if (asUrl && asUrl.startsWith('/rails/active_storage/') && !asUrl.includes('..') && !/['"\\\s()]/.test(asUrl)) {
      return asUrl;
    }

    // Source 2: manual logoUrl from brand_theme in custom_attributes
    const brandTheme = account.custom_attributes?.brand_theme;
    const manualUrl = brandTheme?.logoUrl;
    if (manualUrl && manualUrl.startsWith('/customer-logos/') && !manualUrl.includes('..') && !/['"\\\s()]/.test(manualUrl)) {
      return manualUrl;
    }
  }

  // Source 3: installation-level logo thumbnail
  if (globalConfig.value?.logoThumbnail) {
    return globalConfig.value.logoThumbnail;
  }

  // Source 4: null → render inline SVG fallback below
  return null;
});
</script>

<template>
  <img
    v-if="logoSrc"
    v-bind="attrs"
    :src="logoSrc"
  />
  <svg
    v-else
    v-once
    v-bind="attrs"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clip-path="url(#woot-logo-clip-2342424e23u32098)">
      <path
        d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16Z"
        fill="#2781F6"
      />
      <path
        d="M11.4172 11.4172H7.70831C5.66383 11.4172 4 9.75328 4 7.70828C4 5.66394 5.66383 4 7.70835 4C9.75339 4 11.4172 5.66394 11.4172 7.70828V11.4172Z"
        fill="white"
        stroke="white"
        stroke-width="0.1875"
      />
    </g>
    <defs>
      <clipPath id="woot-logo-clip-2342424e23u32098">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
</template>
